import { ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { useUser, UserButton } from '@clerk/react';
import { cn } from '@/lib/utils';
import {
  Home,
  TrendingUp,
  Sparkles,
  Wallet,
  Bell,
  Star,
  Settings,
  Brain,
  Bookmark,
} from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

const navItems = [
  { path: '/', label: 'Dashboard', icon: Home },
  { path: '/markets', label: 'Markets', icon: TrendingUp },
  { path: '/watchlist', label: 'Watchlist', icon: Bookmark },
  { path: '/analyses', label: 'Analyses', icon: Sparkles },
  { path: '/portfolio', label: 'Portfolio', icon: Wallet },
  { path: '/alerts', label: 'Alerts', icon: Bell },
  { path: '/picks', label: 'Picks', icon: Star },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export function Layout({ children }: LayoutProps) {
  const [location] = useLocation();
  const { isSignedIn, user } = useUser();

  // Don't show layout on landing page when not signed in
  if (!isSignedIn && location === '/') {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-60 lg:flex-col lg:fixed lg:inset-y-0 border-r border-border bg-surface">
        <div className="flex flex-col flex-1 overflow-y-auto">
          {/* Logo */}
          <div className="flex items-center gap-2 px-6 py-6 border-b border-border">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
              <Brain className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold gradient-text">MarketMind</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location === item.path || (item.path !== '/' && location.startsWith(item.path));
              
              return (
                <Link
                  key={item.path}
                  href={item.path}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          {isSignedIn && (
            <div className="px-4 py-4 border-t border-border">
              <div className="flex items-center gap-3">
                <UserButton />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.firstName || 'User'}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.primaryEmailAddress?.emailAddress}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:pl-60">
        <main className="min-h-screen">
          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-surface border-t border-border safe-area-bottom">
          <div className="flex items-center justify-around px-2 py-2">
            {navItems.slice(0, 5).map((item) => {
              const Icon = item.icon;
              const isActive = location === item.path || (item.path !== '/' && location.startsWith(item.path));
              
              return (
                <Link
                  key={item.path}
                  href={item.path}
                  className={cn(
                    'flex flex-col items-center gap-1 px-3 py-2 rounded-lg min-w-[64px] transition-colors',
                    isActive
                      ? 'text-primary'
                      : 'text-muted-foreground'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-xs font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}
